import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, AlertCircle } from "lucide-react";
import {
  useAnnouncementAdmin,
  useUpdateAnnouncement,
} from "../../../services/announcementService";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { ACCEPTABLE_LANGUAGES } from "../../../constants/languages";
import { Checkbox } from "../../../components/ui/checkbox";
import DateTimePicker from "../../../components/ui/DateTimePicker";

const AnnouncementEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const navigate = useNavigate();

  const {
    data: announcement,
    isLoading: isLoadingAnnouncement,
    error,
  } = useAnnouncementAdmin(parseInt(id || "0"), !!id);
  const updateAnnouncementMutation = useUpdateAnnouncement();

  const [formData, setFormData] = useState({
    is_pinned: false,
    visible_from: "",
    visible_to: "",
    translations: ACCEPTABLE_LANGUAGES.map((lang) => ({
      locale: lang,
      title: "",
      body: "",
    })),
  });

  useEffect(() => {
    if (announcement) {
      // Convert ISO date strings to datetime-local format
      const formatDateForInput = (dateString?: string) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };

      setFormData({
        is_pinned: announcement.is_pinned || false,
        visible_from: formatDateForInput(announcement.visible_from),
        visible_to: formatDateForInput(announcement.visible_to),
        translations: ACCEPTABLE_LANGUAGES.map((lang) => {
          const existingTranslation = announcement.translations?.find(
            (t) => t.locale === lang
          );
          return {
            locale: lang,
            title: existingTranslation?.title || "",
            body: existingTranslation?.body || "",
          };
        }),
      });
    }
  }, [announcement]);

  const handleTranslationChange = (
    locale: string,
    field: "title" | "body",
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((translation) =>
        translation.locale === locale
          ? { ...translation, [field]: value }
          : translation
      ),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id) return;

    // Validate that at least one translation has title and body
    const hasValidTranslation = formData.translations.some(
      (translation) => translation.title.trim() && translation.body.trim()
    );

    if (!hasValidTranslation) {
      alert(
        t(
          "admin.announcements.validationError",
          "Please fill in at least one translation with title and body."
        )
      );
      return;
    }

    try {
      await updateAnnouncementMutation.mutateAsync({
        id: parseInt(id),
        announcementData: {
          is_pinned: formData.is_pinned,
          visible_from: formData.visible_from || undefined,
          visible_to: formData.visible_to || undefined,
          translations: formData.translations.filter(
            (translation) => translation.title.trim() && translation.body.trim()
          ),
        },
      });
      navigate(`/${currentLanguage}/admin/announcements`);
    } catch (error) {
      console.error("Failed to update announcement:", error);
    }
  };

  if (isLoadingAnnouncement) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 md:p-6 lg:p-8 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error || !announcement) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 md:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-white dark:bg-gray-800 border-red-200 dark:border-red-900">
            <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
              <AlertCircle className="w-12 h-12 text-red-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t(
                  "admin.announcements.errorLoading",
                  "Error loading announcement"
                )}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {error instanceof Error ? error.message : "Unknown error"}
              </p>
              <Button
                onClick={() =>
                  navigate(`/${currentLanguage}/admin/announcements`)
                }
                variant="outline"
              >
                {t("common.back", "Back")}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 md:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate(`/${currentLanguage}/admin/announcements`)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("common.back", "Back")}
          </Button>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              {t("admin.announcements.edit", "Edit Announcement")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t(
                "admin.announcements.editSubtitle",
                "Update announcement details"
              )}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-gray-900 dark:text-white">
                {t("admin.announcements.basicInfo", "Basic Information")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_pinned"
                  checked={formData.is_pinned}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_pinned: checked === true,
                    }))
                  }
                />
                <Label
                  htmlFor="is_pinned"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
                >
                  {t(
                    "admin.announcements.pinAnnouncement",
                    "Pin this announcement"
                  )}
                </Label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="visible_from">
                    {t("admin.announcements.visibleFrom", "Visible From")}
                  </Label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {t(
                      "admin.announcements.visibleFromHelp",
                      "Leave empty to make visible immediately. Setting a future date will hide the announcement until that time."
                    )}
                  </p>
                  <DateTimePicker
                    id="visible_from"
                    value={formData.visible_from}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        visible_from: value,
                        // Update visible_to minDate if it's before the new visible_from
                        visible_to:
                          prev.visible_to &&
                          value &&
                          new Date(prev.visible_to) < new Date(value)
                            ? ""
                            : prev.visible_to,
                      }))
                    }
                    placeholder={t(
                      "admin.announcements.selectVisibleFrom",
                      "Select visible from date and time (optional)"
                    )}
                    showTimeSelect
                    dateFormat="MMM dd, yyyy h:mm aa"
                  />
                </div>
                <div>
                  <Label htmlFor="visible_to">
                    {t("admin.announcements.visibleTo", "Visible To")}
                  </Label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {t(
                      "admin.announcements.visibleToHelp",
                      "Leave empty for no end date. The announcement will be hidden after this date."
                    )}
                  </p>
                  <DateTimePicker
                    id="visible_to"
                    value={formData.visible_to}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        visible_to: value,
                      }))
                    }
                    placeholder={t(
                      "admin.announcements.selectVisibleTo",
                      "Select visible to date and time (optional)"
                    )}
                    minDate={
                      formData.visible_from
                        ? new Date(formData.visible_from)
                        : undefined
                    }
                    showTimeSelect
                    dateFormat="MMM dd, yyyy h:mm aa"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Translations */}
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-gray-900 dark:text-white">
                {t("admin.announcements.translations", "Translations")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {ACCEPTABLE_LANGUAGES.map((lang) => {
                const translation = formData.translations.find(
                  (t) => t.locale === lang
                );
                if (!translation) return null;

                return (
                  <div
                    key={lang}
                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg space-y-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Badge
                        variant="secondary"
                        className="text-sm font-semibold"
                      >
                        {lang.toUpperCase()}
                      </Badge>
                    </div>
                    <div>
                      <Label htmlFor={`title-${lang}`}>
                        {t("admin.announcements.title", "Title")} *
                      </Label>
                      <Input
                        id={`title-${lang}`}
                        value={translation.title}
                        onChange={(e) =>
                          handleTranslationChange(lang, "title", e.target.value)
                        }
                        placeholder={t(
                          "admin.announcements.titlePlaceholder",
                          "Enter announcement title"
                        )}
                        required={lang === currentLanguage}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`body-${lang}`}>
                        {t("admin.announcements.body", "Body")} *
                      </Label>
                      <textarea
                        id={`body-${lang}`}
                        value={translation.body}
                        onChange={(e) =>
                          handleTranslationChange(lang, "body", e.target.value)
                        }
                        placeholder={t(
                          "admin.announcements.bodyPlaceholder",
                          "Enter announcement body"
                        )}
                        required={lang === currentLanguage}
                        rows={6}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      />
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                navigate(`/${currentLanguage}/admin/announcements`)
              }
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              disabled={updateAnnouncementMutation.isPending}
              className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold shadow-lg hover:shadow-xl"
            >
              {updateAnnouncementMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("common.updating", "Updating...")}
                </>
              ) : (
                t("admin.announcements.update", "Update Announcement")
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AnnouncementEdit;
